import { useState } from 'react';
import { Alert, App, Button, Descriptions, Input, Modal, Typography } from 'antd';
import { WalletOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { errMsg } from '../../../lib/errors';
import { Can } from '../../../rbac/RequirePermission';
import { EMPLOYMENT_REASON_MAX, type Settlement } from './employmentTypes';
import { SETTLEMENT_KIND_LABEL, money, reasonProblem, workDateLabel } from './employmentForm';

/** Permission behind the manual "Credit to Wallet" action (super_admin). */
export const CREDIT_WALLET_PERMISSION = 'listeners.employment.credit_wallet';

/** Query-key prefixes refreshed after a credit: every employment query (settlement lists, attendance, summaries, badges) and the listener's overview. */
export function invalidateAfterCredit(qc: ReturnType<typeof useQueryClient>, listenerId: string | null | undefined): void {
  void qc.invalidateQueries({ queryKey: ['admin', 'employment'] });
  if (listenerId) void qc.invalidateQueries({ queryKey: ['admin', 'listener-overview', listenerId] });
}

const DEFAULT_REASON = 'Employee work earnings';

/**
 * Manual settlement mode: the button that moves one PENDING settlement into
 * the listener wallet. Hidden without `listeners.employment.credit_wallet`;
 * hidden for rows that are not pending. One confirm dialog (employee, amount,
 * work date, editable reason) → `POST /admin/employment/settlements/:id/credit`,
 * which is idempotent on the settlement id, so a retry never pays twice.
 */
export function CreditSettlementButton({
  settlement,
  listenerId,
  listenerName,
  onCredited,
  size = 'small',
}: {
  settlement: Settlement;
  /** For the overview refresh; the global list knows it, a per-listener tab too. */
  listenerId?: string | null;
  listenerName: string;
  onCredited?: (row: Settlement) => void;
  size?: 'small' | 'middle';
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(DEFAULT_REASON);
  const problem = reasonProblem(reason);

  const credit = useMutation({
    mutationFn: async () => (await api.post<Settlement>(`/admin/employment/settlements/${settlement.id}/credit`, { reason: reason.trim() })).data,
    onSuccess: (row) => {
      message.success(`Credited ${money(row.amountPaise ?? settlement.amountPaise)} to ${listenerName}'s wallet — wallet transaction ${row.walletTransactionId ?? row.id}`, 6);
      invalidateAfterCredit(qc, listenerId);
      setOpen(false);
      onCredited?.(row);
    },
    onError: (e: unknown) => message.error(errMsg(e, 'Could not credit the wallet. Nothing was changed — you can try again.')),
  });

  if (settlement.status !== 'pending') return null;
  const negative = settlement.amountPaise !== null && settlement.amountPaise.startsWith('-');

  return (
    <Can permission={CREDIT_WALLET_PERMISSION}>
      <Button size={size} type="primary" icon={<WalletOutlined />} onClick={() => { setReason(DEFAULT_REASON); setOpen(true); }}>
        Credit to Wallet
      </Button>
      <Modal
        title="Confirm Employee Wallet Credit"
        open={open}
        onCancel={credit.isPending ? undefined : () => setOpen(false)}
        maskClosable={false}
        okText="Credit to Wallet"
        okButtonProps={{ disabled: !!problem }}
        confirmLoading={credit.isPending}
        destroyOnHidden
        onOk={() => {
          if (!problem) credit.mutate();
        }}
      >
        <Descriptions column={1} size="small" bordered style={{ marginBottom: 12 }}>
          <Descriptions.Item label="Employee">{listenerName}</Descriptions.Item>
          <Descriptions.Item label="Amount">
            <strong>{money(settlement.amountPaise)}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Work date">{workDateLabel(settlement.workDate)}</Descriptions.Item>
          <Descriptions.Item label="Kind">{SETTLEMENT_KIND_LABEL[settlement.kind] ?? settlement.kind}</Descriptions.Item>
        </Descriptions>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Reason
        </Typography.Text>
        <Input.TextArea
          aria-label="Credit reason"
          rows={2}
          maxLength={EMPLOYMENT_REASON_MAX}
          showCount
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={DEFAULT_REASON}
        />
        {problem && (
          <Typography.Text type="danger" style={{ display: 'block', marginTop: 4 }}>
            {problem}
          </Typography.Text>
        )}
        <Alert
          type={negative ? 'warning' : 'info'}
          showIcon
          style={{ marginTop: 12 }}
          message={
            negative
              ? 'A negative settlement takes the amount BACK from the wallet (ledger reason "Employee work reversal"). It fails if the wallet would go below zero.'
              : 'Adds the amount to the listener wallet as "Employee work earnings". Recorded in the audit log with your name; safe to retry.'
          }
        />
      </Modal>
    </Can>
  );
}
